<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-2">
      <div>
        <h3 class="capitalize-first text-base font-semibold">
          {{ $t('global.what_amount_of') }} ?
        </h3>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <BaseShowEditPriceInput
            v-model="localPayment.amount"
            :label="$t('invoicing.amount')"
            name="amount"
            :errors="errors.amount"
            :currency="currency"
            edition-mode
            required
            @update:model-value:key-up="
              (amount) => (localPayment.amount = amount)
            "
          />
        </div>
        <div
          v-if="
            ![
              PaymentMode.CASH,
              PaymentMode.CARD,
              PaymentMode.CHECK,
              PaymentMode.CAUTION_CHECK,
              PaymentMode.CREDIT_NOTE
            ].includes(payment.payment_mode) || payment.is_payment_deposit
          "
        >
          <SelectPaymentStatuses
            v-model="localPayment.payment_status"
            :errors="errors.payment_status"
            edition-mode
            name="payment_status"
            required
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-2">
      <div>
        <h3 class="capitalize-first text-base font-semibold">Date</h3>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div class="col-start-1">
          <BaseShowEditDatePickerv2
            v-model:date="localPayment.emission_date"
            :label="$t('attributes.emission_date')"
            :errors="errors.emission_date"
            edition-mode
            required
          />
        </div>
        <div v-if="[PaymentMode.CHECK].includes(payment.payment_mode)">
          <div>
            <BaseShowEditDatePickerv2
              v-model:date="localPayment.expected_cashing_date"
              :label="$t('attributes.expected_cashing_date')"
              :errors="errors.expected_cashing_date"
              :today-default="false"
              edition-mode
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="
        [PaymentMode.CHECK, PaymentMode.CAUTION_CHECK].includes(
          payment.payment_mode
        )
      "
      class="flex flex-col gap-2"
    >
      <div>
        <h3 class="capitalize-first text-base font-semibold">
          {{ $t('attributes.check_number') }}
        </h3>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <BaseShowEditInput
            v-model="localPayment.check_number"
            :label="$t('global.number')"
            :edition-mode="true"
            name="zip"
            :required="true"
            :errors="errors.zip"
          />
        </div>
      </div>
    </div>
    <div
      v-if="![PaymentMode.CASH].includes(payment.payment_mode)"
      class="flex flex-col gap-2"
    >
      <div>
        <h3 class="capitalize-first text-base font-semibold">
          Données bancaires
        </h3>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <SelectComponent
            v-if="localPayment.payment_mode_id || payment_modes.length === 0"
            v-model="localPayment.bank_account_id"
            :label="$t('attributes.bank_account_id')"
            name="bank_account_id"
            :edition-mode="true"
            :values="bankAccounts"
            :errors="errors?.bank_account_id"
            :disabled="localPayment.payment_mode === PaymentMode.CREDIT_NOTE"
            :favorite="defaultBankAccountId ? [defaultBankAccountId] : null"
            :favorite-on-top="Boolean(defaultBankAccountId)"
          />
        </div>
        <div>
          <BaseShowEditInput
            v-if="localPayment.payment_mode === PaymentMode.CHECK"
            v-model="localPayment.origin_bank_account"
            :label="$t('attributes.check_issuer_bank')"
            :edition-mode="true"
            name="zip"
            :errors="errors?.origin_bank_account"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { PaymentMode } from '@/assets/enums'
import BaseShowEditPriceInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditPriceInput.vue'
import SelectPaymentStatuses from '@c/InitialStateFormComponent/Enums/SelectPaymentStatuses.vue'
import BaseShowEditDatePickerv2 from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDatePickerv2.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import WalkmanButton from '@c/WalkmanButton.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'

export default {
  name: 'SelectAmount',
  components: {
    SelectComponent,
    BaseShowEditInput,
    BaseShowEditDatePickerv2,
    SelectPaymentStatuses,
    BaseShowEditPriceInput
  },
  props: {
    payment: {
      type: Number,
      required: true
    },
    errors: {
      type: Array,
      required: true
    },
    defaultBankAccountId: {
      type: String,
      required: false,
      default: null
    },
    currency: {
      type: String,
      required: true
    }
  },
  emits: ['update:payment'],
  mounted() {
    if (!this.payment.bank_account_id && this.defaultBankAccountId) {
      this.$emit('update:payment', {
        ...this.payment,
        bank_account_id: this.defaultBankAccountId
      })
    }
  },
  computed: {
    ...mapGetters({
      payment_modes: 'metadata/getPaymentModesMetadata',
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    PaymentMode() {
      return PaymentMode
    },
    localPayment: {
      get() {
        return this.payment
      },
      set(value) {
        this.$emit('update:payment', value)
      }
    },
    selectedPaymentMode() {
      return this.payment_modes.find(
        (payment_mode) => this.payment.payment_mode === payment_mode.id
      )
    },
    paymentModes() {
      if (this.paymentMode && this.paymentMode.id !== 'custom') {
        return this.initialMetadata.enums?.payment_modes
          ?.filter((mode) => this.paymentMode.payment_modes.includes(mode.id))
          ?.sort((a, b) => a.name.localeCompare(b.name))
      } else {
        return this.initialMetadata.enums?.payment_modes
          ?.filter((mode) => {
            if (!this.dossier && mode.id === 'LEV') {
              return false
            }
            return true
          })
          .sort((a, b) => a.name.localeCompare(b.name))
      }
    },
    bankAccounts() {
      return this.initialMetadata.models.bankAccounts?.length > 0
        ? this.initialMetadata.models.bankAccounts
        : (this.model?.bank_accounts ?? [])
    }
  }
}
</script>
